import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, IndianRupee, MessageCircle } from 'lucide-react';
import Icon from './Icon';
import { WhatsAppGlyph } from './CtaButtons';
import { PICKER_SEVAS, whatsappForSelection } from '../../lib/constants';
import { track } from '../../lib/analytics';

const OPEN_EVENT = 'omvedha:open-seva-picker';

// Dispatch helper used by all "Chat on WhatsApp" buttons on the page.
// options:
//   - initial: { [sevaName]: { selected: bool, amount: number } } — preselect these
//   - onSelect: (list, total) => void — if provided, called on Continue and picker closes
//                                       without opening WhatsApp (form-return mode)
export const openSevaPicker = (source = 'unknown', options = {}) => {
  document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { source, ...options } }));
};

const defaultsFor = (list) =>
  list.reduce((acc, s) => {
    acc[s.name] = { selected: false, amount: s.custom ? s.defaultAmount : Number(s.price || 0) };
    return acc;
  }, {});

const SevaPicker = () => {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState('unknown');
  const [onSelectCb, setOnSelectCb] = useState(null);
  const [state, setState] = useState(() => defaultsFor(PICKER_SEVAS));

  useEffect(() => {
    const onOpen = (e) => {
      const detail = e?.detail || {};
      setSource(detail.source || 'unknown');
      setOnSelectCb(() => detail.onSelect || null);
      const base = defaultsFor(PICKER_SEVAS);
      if (detail.initial) {
        Object.entries(detail.initial).forEach(([name, val]) => {
          if (base[name]) base[name] = { ...base[name], ...val };
        });
      }
      setState(base);
      setOpen(true);
      track('seva_picker_open', detail.source || 'unknown');
    };
    document.addEventListener(OPEN_EVENT, onOpen);
    return () => document.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const toggle = (name) =>
    setState((s) => ({ ...s, [name]: { ...s[name], selected: !s[name].selected } }));

  const setAmount = (name, v) =>
    setState((s) => ({ ...s, [name]: { ...s[name], amount: Math.max(0, Number(String(v).replace(/[^\d]/g, '') || 0)) } }));

  const { selectedList, total } = useMemo(() => {
    const list = PICKER_SEVAS
      .filter((s) => state[s.name]?.selected)
      .map((s) => ({ ...s, amount: state[s.name].amount || 0 }));
    return { selectedList: list, total: list.reduce((sum, s) => sum + (s.amount || 0), 0) };
  }, [state]);

  const invalidAnnaDana = PICKER_SEVAS.some(
    (s) => s.custom && state[s.name]?.selected && (state[s.name].amount || 0) < (s.min || 0)
  );

  const proceed = () => {
    if (!selectedList.length || invalidAnnaDana) return;
    if (onSelectCb) {
      track('seva_picker_return', `picker:${source}`);
      onSelectCb(selectedList, total);
      setOpen(false);
      return;
    }
    track('cta_whatsapp', `picker:${source}`);
    const url = whatsappForSelection(selectedList, total);
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="seva-picker"
          className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', damping: 24, stiffness: 260 }}
            className="relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-3xl bg-ivory shadow-2xl sm:my-6 sm:max-w-4xl sm:rounded-3xl"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 border-b border-gold/25 bg-white px-5 py-5 sm:px-8">
              <div>
                <p className="font-sans text-[10px] uppercase tracking-[0.24em] text-gold-deep">Sponsor a Seva</p>
                <h3 className="mt-1 font-serif text-xl text-ink sm:text-2xl">
                  Choose the sevas you wish to sponsor
                </h3>
                <p className="mt-1 font-sans text-sm text-ink/60">Pick as many as you like. We&rsquo;ll send it to WhatsApp with the total.</p>
              </div>
              <button
                type="button"
                data-testid="seva-picker-close"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold/30 bg-ivory text-ink/70 transition-colors hover:bg-gold/10"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Grid */}
            <div className="flex-1 overflow-y-auto bg-[linear-gradient(180deg,#FDFBF7,#F7EFD9)] px-5 py-6 sm:px-8">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {PICKER_SEVAS.map((s) => {
                  const st = state[s.name];
                  const isSelected = st?.selected;
                  const isCustom = !!s.custom;
                  const tid = `picker-${s.name.replace(/\s+/g, '-').toLowerCase()}`;
                  return (
                    <div
                      key={s.name}
                      data-testid={tid}
                      onClick={() => toggle(s.name)}
                      className={`group relative cursor-pointer rounded-2xl border p-5 transition-all ${
                        isSelected
                          ? 'border-gold bg-white shadow-[0_20px_50px_-30px_rgba(179,139,34,0.6)]'
                          : 'border-gold/20 bg-white/80 hover:border-gold/45'
                      }`}
                    >
                      <span
                        className={`absolute right-4 top-4 flex h-5 w-5 items-center justify-center rounded border transition-colors ${
                          isSelected ? 'border-maroon bg-maroon text-ivory' : 'border-gold/40 bg-ivory'
                        }`}
                        data-testid={`${tid}-check`}
                      >
                        {isSelected && (
                          <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
                            <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.4 7.4a1 1 0 01-1.4 0L3.3 9.5a1 1 0 011.4-1.4L8.6 12l6.7-6.7a1 1 0 011.4 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </span>

                      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl border border-gold/30 bg-[radial-gradient(circle_at_50%_35%,#FBF3D8,#FFFFFF)] text-gold-deep">
                        <Icon name={s.icon} className="h-5 w-5" strokeWidth={1.6} />
                      </div>

                      <h4 className="pr-6 font-serif text-lg leading-tight text-maroon">
                        {s.displayName || s.name}
                      </h4>
                      {s.time && <p className="mt-1 font-sans text-xs text-ink/55">{s.time}</p>}

                      <div className="mt-4 h-px w-full border-t border-dashed border-gold/25" />

                      {isCustom ? (
                        <div className="mt-4">
                          <p className="font-sans text-[11px] uppercase tracking-[0.14em] text-ink/55">Your offering</p>
                          <div className="mt-1.5 flex items-center gap-2">
                            <span className="text-maroon">₹</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              data-testid={`${tid}-amount`}
                              value={st.amount || ''}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setAmount(s.name, e.target.value)}
                              className="w-24 rounded-lg border border-gold/30 bg-white px-3 py-1.5 font-serif text-lg text-ink outline-none focus:border-gold"
                            />
                          </div>
                          <p className="mt-1 font-sans text-[11px] text-ink/45">Minimum ₹{s.min}</p>
                          {isSelected && (st.amount || 0) < (s.min || 0) && (
                            <p className="mt-1 font-sans text-[11px] font-medium text-red-600">Please enter at least ₹{s.min}.</p>
                          )}
                        </div>
                      ) : (
                        <p className="mt-4 font-sans text-sm text-ink/55">
                          Offering <span className="ml-1 font-serif text-lg text-maroon">₹{Number(s.price).toLocaleString('en-IN')}</span>
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sticky footer */}
            <div className="border-t border-gold/25 bg-white px-5 py-4 sm:px-8">
              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <IndianRupee className="h-5 w-5 text-gold-deep" />
                  <div>
                    <p className="font-sans text-[11px] uppercase tracking-[0.14em] text-ink/55">
                      {selectedList.length ? `${selectedList.length} seva${selectedList.length > 1 ? 's' : ''} selected` : 'Select at least one seva'}
                    </p>
                    <p className="font-serif text-2xl text-ink" data-testid="seva-picker-total">
                      ₹{total.toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="seva-picker-continue"
                  onClick={proceed}
                  disabled={!selectedList.length || invalidAnnaDana}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-6 py-3.5 font-sans text-sm font-semibold uppercase tracking-[0.15em] text-white shadow-[0_10px_25px_-8px_rgba(37,211,102,0.6)] transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  {onSelectCb ? (
                    <>Confirm Selection</>
                  ) : (
                    <>
                      <WhatsAppGlyph className="h-5 w-5" /> Continue on WhatsApp
                      {selectedList.length > 0 && <MessageCircle className="hidden h-4 w-4 opacity-70 sm:inline" />}
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SevaPicker;
